import type { RequestAction } from "trystero";
import type { RecoveryDescriptor } from "./online-room-types.js";
import {
  type CertifiedCheckpoint,
  type CheckpointAcknowledgment,
  RECOVERY_PACKET_MAX_BYTES,
  type RecoveryCheckpoint,
  type RecoveryStateMachine,
} from "./recovery-protocol.js";
import { decodeBoundedRoomPacket, encodeRoomPacket } from "./room-packet.js";
import { OnlineRoomAuthority } from "./room-protocol.js";
import type { RoomHello } from "./room-types.js";
import type { ConflictConfig } from "./types.js";

export async function certifyOnlineCheckpoint(
  state: RecoveryStateMachine,
  authority: OnlineRoomAuthority,
  descriptor: RecoveryDescriptor,
  peers: readonly { readonly peerId: string; readonly hello: RoomHello }[],
  action: RequestAction<string, string>,
  publish: (certified: CertifiedCheckpoint) => Promise<void>,
): Promise<void> {
  const checkpoint = authority.recoveryCheckpoint(
    descriptor.hostMemberId,
    descriptor.voters,
    descriptor.membershipVersion,
  );
  const own = state.offerCheckpoint(checkpoint);
  const local = state.recordCheckpointAcknowledgment(own);
  if (local) await publish(local);
  const replies = await Promise.allSettled(
    peers.map(async (peer) => ({
      acknowledgment: decodeBoundedRoomPacket<CheckpointAcknowledgment>(
        await action.request(encodeRoomPacket(checkpoint), {
          target: peer.peerId,
          timeoutMs: 2_000,
        }),
        RECOVERY_PACKET_MAX_BYTES,
      ),
      memberId: peer.hello.sessionId,
    })),
  );
  for (const reply of replies) {
    if (reply.status !== "fulfilled") continue;
    if (reply.value.acknowledgment.memberId !== reply.value.memberId) continue;
    const certified = state.recordCheckpointAcknowledgment(reply.value.acknowledgment);
    if (certified) await publish(certified);
  }
}

export function recoverOnlineAuthority(
  config: ConflictConfig | undefined,
  selfPeerId: string,
  hello: RoomHello,
  checkpoint: RecoveryCheckpoint,
  authorityEpoch: number,
  peers: readonly { readonly peerId: string; readonly hello: RoomHello }[],
): OnlineRoomAuthority {
  if (!config) throw new TypeError("Recovery is missing the locked room configuration");
  return OnlineRoomAuthority.recover(config, selfPeerId, hello, checkpoint, authorityEpoch, peers);
}
